import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { useTranslation } from "react-i18next";
import preprocessDataForExport from "../../../controllers/customControllerForCS/preprocessDataForExport";
import { useContext } from "react";
import Context from "../../../Context";
import MarkAbsentOrNotCell from "./MarkAbsentOrNotCell";
import CustomButton from "../../helperComponents/CustomButton";
import AbsenceReplacementPopup from "../absenceReplacementPopup/AbsenceReplacementPopup";
import { BiEdit } from "react-icons/bi";
import ObservationCell from "./ObservationCell";

export default function TableRenderForAH({
  fields,
  items,
  setItems,
  refresh,
  date,
  siteID,
  shift,
  slotQuota,
  slotTargets,
  vacantSlotAssignments,
  readOnly,
}) {
  const { setForm } = useContext(Context);
  const { t } = useTranslation();

  let unprocessedItems = JSON.parse(JSON.stringify(items));

  let columns = [
    {
      field: "PRS",
      headerName: "PRS",
      width: 84,
      sortable: false,
      renderCell: (params) =>
        params.row.__isVacantSlot && !params.row.__vacantReplacementUserID ? null : (
          <MarkAbsentOrNotCell
            readOnly={readOnly}
            updateRow={updateRow}
            date={date}
            value={params.row["Absence"]}
            userID={params.row["User Id"]}
            target="PRS"
            isRestDay={params.row.__isRest}
          />
        ),
    },
    {
      field: "ABS",
      headerName: "ABS",
      width: 84,
      sortable: false,
      renderCell: (params) =>
        params.row.__isVacantSlot && !params.row.__vacantReplacementUserID ? null : (
          <MarkAbsentOrNotCell
            readOnly={readOnly}
            updateRow={updateRow}
            date={date}
            value={params.row["Absence"]}
            userID={params.row["User Id"]}
            target="ABS"
            isRestDay={params.row.__isRest}
          />
        ),
    },
    {
      field: "Repos",
      headerName: "Repos",
      width: 110,
      sortable: false,
      renderCell: (params) =>
        params.row.__isVacantSlot && !params.row.__vacantReplacementUserID
          ? "-"
          : params.row.__isRest
          ? "OUI"
          : "NON",
      cellClassName: ({ row }) => (row.__isRest ? "rest-row-cell" : ""),
    },
  ];

  let rows = [];

  for (let fieldName of fields) {
    let fieldWidth = t(fieldName).length * 11;
    let max = 360;
    let minimum = 170;
    if (fieldWidth > max) fieldWidth = max;
    if (fieldWidth < minimum) fieldWidth = minimum;

    const column = {
      field: fieldName,
      headerName: t(fieldName),
      width: fieldWidth,
      cellClassName: ({ row }) => {
        if (row.__isVacant) return "vacant-row-cell";
        if (row.__isRest) return "rest-row-cell";

        if (
          fieldName === "ABS" ||
          fieldName === "PRS" ||
          fieldName === "Day Of Rest"
        ) {
          return row.__isAbsent ? "absent-block" : "";
        }
        if (
          fieldName === "Employee Responsibility" ||
          fieldName === "Profile First Name" ||
          fieldName === "Profile Last Name"
        ) {
          return row.__isAbsent ? "absent-block" : "";
        }
        if (row.__isPresent) return "present-row";
        return "";
      },
    };

    if (fieldName === "Profile Registration Number") {
      column.renderCell = (params) => {
        if (!params.row.__isVacantSlot) return params.value || "";

        const registrationNumber =
          params.row["Profile Registration Number"] || "";

        if (readOnly) return registrationNumber || "-";

        return (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span>{registrationNumber || "-"}</span>
            <CustomButton
              style={{
                border: "none",
                height: "28px",
                width: "38px",
                justifyContent: "center",
                alignItems: "center",
                padding: "0",
              }}
              icon={<BiEdit />}
              onClick={() => {
                openVacantSlotSelector(params);
              }}
            />
          </div>
        );
      };
    }

    columns.push(column);
  }

  columns.push({
    field: "Ajouter remplaçant",
    headerName: "Remplaçant",
    width: 180,
    renderCell: (params) =>
      readOnly ||
      params.row.__isPresent ||
      params.row.__isVacant ||
      params.row.__isVacantSlot ? null : (
        <CustomButton
          style={{
            border: "none",
            height: "30px",
            marginTop: "-5px",
            width: "50px",
            justifyContent: "center",
            alignItems: "center",
          }}
          icon={<BiEdit />}
          onClick={() => {
            addReplacementButton(params);
          }}
        />
      ),
  });

  columns.push({
    field: "Observation",
    headerName: "Observation",
    width: 320,
    sortable: false,
    renderCell: (params) => (
      params.row.__isVacantSlot && !params.row.__vacantReplacementUserID ? null : (
        <ObservationCell
          readOnly={readOnly}
          value={params.row["Presence Observation"] || ""}
          date={date}
          userID={params.row["User Id"]}
          onLocalChange={
            params.row.__isVacantSlot
              ? null
              : (nextValue) =>
                  updateRow({
                    userID: params.row["User Id"],
                    field: "Presence Observation",
                    value: nextValue,
                  })
          }
        />
      )
    ),
    cellClassName: ({ row }) =>
      row.__isVacant ? "vacant-row-cell" : row.__isRest ? "rest-row-cell" : "",
  });

  items = preprocessDataForExport(items);

  const shiftPriority = {
    "12H-DAY": 1,
    "12H-NIGHT": 2,
    NONE: 3,
    "24H-DAY": 4,
  };
  items.sort((a, b) => {
    const pa = shiftPriority[a["Shift"]] ?? 99;
    const pb = shiftPriority[b["Shift"]] ?? 99;
    if (pa !== pb) return pa - pb;
    const ra = getResponsibilitySortKey(a["Employee Responsibility"]);
    const rb = getResponsibilitySortKey(b["Employee Responsibility"]);
    if (ra.order !== rb.order) return ra.order - rb.order;
    if (ra.value !== rb.value) return ra.value.localeCompare(rb.value);
    return (a["Profile Last Name"] || "").localeCompare(
      b["Profile Last Name"] || ""
    );
  });

  const hasShiftQuota =
    shift === "12H-DAY" || shift === "12H-NIGHT" || !shift;

  for (let item of items) {
    const presenceStatus = item["Presence Status"];
    if (presenceStatus === "PRESENT") {
      item["Absence"] = "NON";
      item.__absenceTouched = true;
    } else if (presenceStatus === "ABSENT") {
      item["Absence"] = "OUI";
      item.__absenceTouched = true;
    }

    const rawAbsence = item["Absence"];
    const absenceTouched = item.__absenceTouched;
    if (!absenceTouched) {
      if (
        rawAbsence === false ||
        rawAbsence === null ||
        rawAbsence === undefined ||
        rawAbsence === "" ||
        rawAbsence === "NON"
      ) {
        item["Absence"] = null;
      } else if (rawAbsence === true) {
        item["Absence"] = "OUI";
      }
    }

    const restRaw = item["Is Rest Day"];
    const restStatusRaw = item["Rest Status"];
    const isRestDay =
      restRaw === true ||
      restRaw === "OUI" ||
      restRaw === "Oui" ||
      restStatusRaw === "OUI" ||
      restStatusRaw === "Oui";

    item["Repos"] = isRestDay ? "OUI" : "NON";

    let newItem = transformObjectValues(item);
    const rowMeta = {
      __isRest: isRestDay,
      __isVacant: false,
      __isVacantSlot: false,
      __isAbsent: !isRestDay && item["Absence"] === "OUI",
      __isPresent: !isRestDay && item["Absence"] === "NON",
    };
    rows.push({ id: item["User Id"], ...newItem, ...rowMeta });
  }

  if (hasShiftQuota) {
    const slotAssignmentMap = buildVacantSlotAssignmentMap(vacantSlotAssignments);
    const targets =
      Array.isArray(slotTargets) && slotTargets.length
        ? slotTargets
        : siteID &&
          (shift === "12H-DAY" || shift === "12H-NIGHT") &&
          Number.isFinite(Number(slotQuota))
        ? [
            {
              siteID,
              siteName: "",
              zoneName: "",
              shift,
              quota: Number(slotQuota),
              assignedCount: rows.filter(
                (row) => row["Site Id"] === siteID && row["Shift"] === shiftToLabel(shift)
              ).length,
            },
          ]
        : [];

    for (const target of targets) {
      const targetShift = target?.shift || shift;
      const deficit = Math.max(
        0,
        Number(target?.quota || 0) - Number(target?.assignedCount || 0)
      );
      for (let slotIndex = 1; slotIndex <= deficit; slotIndex++) {
        const assignment =
          slotAssignmentMap[
            `${target.siteID || ""}__${targetShift || ""}__${slotIndex}`
          ] || null;
        rows.push(
          createVacantRow({
            shift: targetShift,
            siteID: target.siteID,
            siteName: target.siteName,
            zoneName: target.zoneName,
            slotIndex,
            assignment,
          })
        );
      }
    }
  }

  rows.sort(sortPresenceRows);

  return (
    <Paper className="employee-presence-table" sx={{ height: 800, width: "100%" }}>
      <DataGrid
        showCellVerticalBorder
        rows={rows}
        columns={columns}
        hideFooterPagination={true}
        sx={{
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "var(--surfaceSolid)",
          },
        }}
        getRowClassName={(params) => {
          let classNames =
            params.row["Profile Is Active"] === "NON"
              ? "inactive-row"
              : "active-row";

          const currentRowIndex = rows.findIndex((r) => r.id === params.id);
          const prevRow = rows[currentRowIndex - 1];
          if (prevRow && prevRow["Zone Name"] !== params.row["Zone Name"]) {
            classNames += " hard-border-row";
          }

          if (prevRow && prevRow["Zone Name"] === params.row["Zone Name"]) {
            classNames += " repeated-zone-row";
          }

          if (params.row["Replacement User Registration Number"]) {
            classNames += " absence-replacement-added-row";
          }

          if (params.row.__isVacant) {
            classNames += " vacant-user-row";
          } else if (params.row.__isRest) {
            classNames += " rest-user-row";
          } else if (params.row["Absence"] === "OUI") {
            classNames += " absent-user-row";
          }

          return classNames;
        }}
      />
    </Paper>
  );

  function addReplacementButton(params) {
    setForm({
      title: `Ajouter remplaçant`,
      component: (
        <AbsenceReplacementPopup
          date={date}
          userID={params.id}
          mode={params.row.__isRest ? "rest" : "absence"}
          callback={callback}
        />
      ),
    });
  }

  function openVacantSlotSelector(params) {
    if (!params.row.__isVacantSlot) return;
    setForm({
      title: "Affecter un agent",
      component: (
        <AbsenceReplacementPopup
          date={date}
          mode={"vacant"}
          vacantSlot={{
            siteID: params.row["Site Id"],
            shift: params.row.__slotShift,
            slotIndex: params.row.__slotIndex,
          }}
          onBeforeAssign={(doc) => warnDuplicateCandidate(doc, params.row)}
          callback={callback}
        />
      ),
    });
  }

  function warnDuplicateCandidate(doc, currentRow) {
    const candidateID = doc?._id ? String(doc._id).toUpperCase() : "";
    const candidateRegistration = doc?.registrationNumber
      ? String(doc.registrationNumber).toUpperCase()
      : "";

    if (!candidateID && !candidateRegistration) return true;

    const duplicateFound = rows.some((row) => {
      if (row.id === currentRow.id) return false;
      const rowUserID = row["User Id"] ? String(row["User Id"]).toUpperCase() : "";
      const rowVacantReplacementID = row.__vacantReplacementUserID
        ? String(row.__vacantReplacementUserID).toUpperCase()
        : "";
      const rowRegistration = row["Profile Registration Number"]
        ? String(row["Profile Registration Number"]).toUpperCase()
        : "";
      const replacementRegistration = row["Replacement User Registration Number"]
        ? String(row["Replacement User Registration Number"]).toUpperCase()
        : "";

      return (
        (candidateID && rowUserID === candidateID) ||
        (candidateID && rowVacantReplacementID === candidateID) ||
        (candidateRegistration && rowRegistration === candidateRegistration) ||
        (candidateRegistration &&
          replacementRegistration === candidateRegistration)
      );
    });

    if (!duplicateFound) return true;
    return window.confirm(
      "Attention: cet agent semble déjà présent sur la date/shift. Continuer quand même ?"
    );
  }

  function callback() {
    refresh();
  }

  function transformObjectValues(obj) {
    const result = {};

    for (const key in obj) {
      const value = obj[key];

      if (value === null) {
        result[key] = "";
      } else if (typeof value === "string") {
        result[key] = value.toUpperCase();
      } else if (typeof value === "boolean") {
        result[key] = value ? "OUI" : "NON";
      } else {
        result[key] = String(value).toUpperCase();
      }
    }

    return result;
  }

  function updateRow({ field, value, userID }) {
    if (!userID) return;
    let newItems = JSON.parse(JSON.stringify(unprocessedItems));
    for (let item of newItems) {
      if (item["User Id"].toUpperCase() === userID) {
        item[field] = value;

        if (field === "Absence") {
          item.__absenceTouched = true;
          if (value !== "OUI") {
            item["Replacement User Registration Number"] = null;
            item["Replacement User First Name"] = null;
            item["Replacement User Last Name"] = null;
          }
        }
      }
    }

    const foundItem = newItems.some(
      (item) => item["User Id"]?.toUpperCase() === userID
    );

    if (!foundItem) {
      refresh();
      return;
    }

    setItems(newItems);
  }
}

function createVacantRow({ shift, siteID, siteName, zoneName, slotIndex, assignment }) {
  const hasReplacement = Boolean(assignment?.replacementUserID);
  const absence =
    assignment?.presenceStatus === "PRESENT"
      ? "NON"
      : assignment?.presenceStatus === "ABSENT"
      ? "OUI"
      : "";

  return {
    id: `VACANT-${siteID || "NA"}-${shift || "NA"}-${slotIndex}`,
    __isVacant: !hasReplacement,
    __isVacantSlot: true,
    __isRest: false,
    __isAbsent: hasReplacement && absence === "OUI",
    __isPresent: hasReplacement && absence === "NON",
    __slotIndex: slotIndex,
    __slotShift: shift,
    __vacantReplacementUserID: assignment?.replacementUserID || null,
    "User Id": assignment?.replacementUserID || null,
    "Site Id": siteID || "",
    "Zone Name": (zoneName || "").toUpperCase(),
    "Site Place Name": (siteName || "").toUpperCase(),
    "Profile Registration Number":
      assignment?.replacementUserRegistrationNumber || "",
    "Profile Last Name": assignment?.replacementUserLastName || "",
    "Profile First Name": assignment?.replacementUserFirstName || "",
    "Employee Responsibility": "",
    Shift: shiftToLabel(shift),
    Absence: absence,
    Repos: "-",
    "Presence Observation": assignment?.presenceObservation || "",
    Observation: assignment?.presenceObservation || "",
  };
}

function sortPresenceRows(a, b) {
  const zoneA = (a["Zone Name"] || "").toString().toUpperCase();
  const zoneB = (b["Zone Name"] || "").toString().toUpperCase();
  if (zoneA !== zoneB) return zoneB.localeCompare(zoneA);

  const siteA = (a["Site Place Name"] || "").toString().toUpperCase();
  const siteB = (b["Site Place Name"] || "").toString().toUpperCase();
  if (siteA !== siteB) return siteA.localeCompare(siteB);

  if (a.__isVacantSlot !== b.__isVacantSlot) return a.__isVacantSlot ? 1 : -1;

  const shiftPriority = { JOUR: 1, NUIT: 2, ALTERNE: 3 };
  const shiftA = shiftPriority[(a["Shift"] || "").toUpperCase()] || 99;
  const shiftB = shiftPriority[(b["Shift"] || "").toUpperCase()] || 99;
  if (shiftA !== shiftB) return shiftA - shiftB;

  if (a.__isVacantSlot && b.__isVacantSlot) {
    if (a.__isVacant !== b.__isVacant) return a.__isVacant ? 1 : -1;
    const slotA = Number(a.__slotIndex || 0);
    const slotB = Number(b.__slotIndex || 0);
    if (slotA !== slotB) return slotA - slotB;
  }

  const lastA = (a["Profile Last Name"] || "").toString().toUpperCase();
  const lastB = (b["Profile Last Name"] || "").toString().toUpperCase();
  if (lastA !== lastB) return lastA.localeCompare(lastB);

  const firstA = (a["Profile First Name"] || "").toString().toUpperCase();
  const firstB = (b["Profile First Name"] || "").toString().toUpperCase();
  return firstA.localeCompare(firstB);
}

function getResponsibilitySortKey(responsibility) {
  const raw = (responsibility || "").toString().trim();
  const upper = raw.toUpperCase();
  const zoulouMatch = upper.match(/ZOULOU\s*([0-9]+)/);
  if (zoulouMatch) {
    const num = parseInt(zoulouMatch[1], 10);
    return {
      order: Number.isNaN(num) ? 9999 : num,
      value: upper,
    };
  }
  return {
    order: 10000,
    value: upper,
  };
}

function shiftToLabel(shift) {
  if (shift === "12H-DAY") return "JOUR";
  if (shift === "12H-NIGHT") return "NUIT";
  if (shift === "NONE") return "ALTERNE";
  return shift || "";
}

function buildVacantSlotAssignmentMap(list) {
  const map = {};
  if (!Array.isArray(list)) return map;
  for (const item of list) {
    const siteID = item?.siteID || "";
    const shift = item?.shift || "";
    const slotIndex = Number(item?.slotIndex || 0);
    if (!siteID || !shift || !slotIndex) continue;
    map[`${siteID}__${shift}__${slotIndex}`] = item;
  }
  return map;
}
